<template lang="pug">
  y-dialog(v-model="showModal" fill-height fluid scrollable persistent transition="fade-transition" max-width="600px" :fullscreen="$vuetify.breakpoint.xsOnly")

    v-card(flat)
      v-card-title.px-5
        v-row(no-gutters align="center")
          v-col.shrink
            v-btn.ml-n3(text icon @click="closeDialog()" height)
              eva-icon(name="arrow-back-outline" :fill="$vuetify.theme.themes[$theme].primary500" width="24" height="24")
          v-col.ml-3
            p.mb-0.h6.primary500--text {{ isEditing ? $t('store_address') : $t('setup_store_address') }}

      v-layout.my-6(v-if="isFetching")
        v-row.fill-height.ma-0(align='center' justify='center')
          v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)

      v-card-text.pa-0(v-else)
        v-form(ref="form")
          v-card.pa-4.mx-4.mb-12(outlined flat)
            .s1 {{ $t('enter_store_address') }}
            .c1.basic600--text {{ $t('enter_store_address_text') }}
            .address.mt-4
              div.label.basic600--text.text-uppercase {{$t('address_1.label')}}
              v-text-field.mt-2(v-model="address.address_1" :placeholder="$t('address_1.placeholder')" dense outlined height="40" background-color="basic200" :rules="rules.not_empty")
              div.label.basic600--text.text-uppercase {{$t('address_2.label')}}
              v-text-field.mt-2(v-model="address.address_2" :placeholder="$t('address_2.placeholder')" dense outlined height="40" background-color="basic200" :rules="rules.not_empty")
              div.label.basic600--text.text-uppercase {{$t('postcode.label')}}
              v-text-field.mt-2(v-model="address.postcode" :placeholder="$t('postcode.placeholder')" dense outlined height="40" background-color="basic200" :rules="rules.not_empty" @keyup="fetchPostcodes(address.postcode)")
              div.label.basic600--text.text-uppercase {{$t('city.label')}}
              v-text-field.mt-2(v-model="address.city" :placeholder="$t('city.placeholder')" dense outlined height="40" background-color="basic200" :rules="rules.not_empty" readonly :loading="isFetchingAddress")
              div.label.basic600--text.text-uppercase {{$t('state.label')}}
              v-text-field.mt-2(v-model="address.state" :placeholder="$t('state.placeholder')" dense outlined height="40" background-color="basic200" :rules="rules.not_empty" readonly :loading="isFetchingAddress")
              div.label.basic600--text.text-uppercase {{$t('country.label')}}
              v-text-field.mt-2(v-model="address.country" :placeholder="$t('country.placeholder')" dense outlined height="40" background-color="basic200" :rules="rules.not_empty" readonly :loading="isFetchingAddress")

            .company-phone
              div.label.basic600--text.text-uppercase {{$t('phone.label')}}
              v-text-field.mt-2(v-model='address.phone' :placeholder="$t('phone.placeholder')" dense outlined height="40" type="tel" background-color="basic200" :rules="rules.not_empty")  

        v-layout.ma-4(v-if="!isEditing")
          v-spacer
          v-btn(color='textColorAccent' depressed :loading="isLoading" @click="clickSave")
            .button-AA-medium.basic100--text {{ $t('save_and_continue') }}

</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'StorePickupAddress',
  props: ['open', 'company', 'isEditing', 'courier'],
  data: () => ({
    isFetching: false,
    same_for_awb_address: false,
    address: {
      address_1: '',
      address_2: '',
      postcode: '',
      city: '',
      state: '',
      country: '',
      lat: null,
      lng: null,
      phone: ''
    },
    isFetchingAddress: false,
    isLoading: false,
    rules: {
      not_empty: [val => (val || '').length > 0 || 'This field is required']
    }
  }),
  computed: {
    showModal: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    }
  },
  mounted () {
    this.fetchShippingCourier()
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    fetchShippingCourier() {
      this.isFetching = true
      this.$axios.get(`/shipping/settings/${this.company.slug}/storepickup`)
        .then((response) => {
          this.address = response.data.pickup_address
        }).catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    closeDialog () {
      if (!this.isEditing) {
        this.showModal = !this.showModal
        return
      }
      if (!this.$refs.form.validate()) {
        return
      }
      this.courier.pickup_address = this.address
      this.showModal = !this.showModal
    },
    fetchPostcodes (val) {
      if (!val) {
        return
      }
      if (val.length !== 5) {
        return
      }
      this.isFetchingAddress = true
      this.$axios.get(`/shipping/postcodes?postcode=${this.address.postcode}`).then((response) => {
        const addresses = response.data
        if (addresses.length > 0 && addresses[0].postcode) {
          this.$set(this.address, 'city', addresses[0].city)
          this.$set(this.address, 'state', addresses[0].state)
          this.$set(this.address, 'country', addresses[0].country)
        }
      })
      .catch(() => {
      })
      .finally(() => {
        this.isFetchingAddress = false
      })
    },
    clickSave() {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      var params = {
        pickup_address: this.address
      }
      this.$axios.post(`/shipping/settings/${this.company.slug}/storepickup/`, params)
        .then(() => {
          this.showModal = false
          this.$emit('finished')
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
</style>

<i18n>
{
  "en": {
    "store_address": "Store address",
    "setup_store_address": "Setup store pickup",
    "enter_store_address": "Enter your store address",
    "enter_store_address_text": "This is where customer would go to collect their orders.",
    "phone": {
      "label": "Phone number",
      "placeholder": "Enter phone number"
    },
    "registration_number": {
      "label": "Company Registration No",
      "placeholder": "Write your company registration number here"
    },
    "address_1": {
      "label": "Address Line 1",
      "placeholder": "e.g. K-3-01, Conezion, Persiaran IRC3"
    },
    "address_2": {
      "label": "Address Line 2",
      "placeholder": "e.g. IOI Resort City"
    },
    "postcode": {
      "label": "Postcode",
      "placeholder": "e.g. 62502"
    },
    "city": {
      "label": "City",
      "placeholder": "e.g. Putrajaya"
    },
    "state": {
      "label": "State",
      "placeholder": "e.g. Wilayah Persekutuan Putrajaya"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Malaysia"
    }
  },
  "id": {
    "store_address": "Alamat toko",
    "setup_store_address": "Kelola penngambilan paket di toko",
    "enter_store_address": "Tulis alamat toko",
    "enter_store_address_text": "Pelanggan akan mengambil paket mereka di alamat toko ini",
    "phone": {
      "label": "Nomor Telepon",
      "placeholder": ""
    },
    "registration_number": {
      "label": "Nomor Registrasi Perusahaan",
      "placeholder": ""
    },
   "address_1": {
      "label": "Nama Jalan, Gedung, No Rumah, RT/RW, Kelurahan",
      "placeholder": "e.g. Jalan Prof. Joko Sutono Sh No.999, RT.14/RW.6, Petogogan, "
    },
    "address_2": {
      "label": "Kecamatan",
      "placeholder": "Kec. Kby. Baru"
    },
    "postcode": {
      "label": "Kode Pos",
      "placeholder": "12160"
    },
    "city": {
      "label": "Kota",
      "placeholder": "Kota Jakarta Selatan"
    },
    "state": {
      "label": "Provinsi",
      "placeholder": "DKI Jakarta"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Indonesia"
    }
  }
}
</i18n>
